import { memo, useMemo, useState } from 'react';
import { EdgeProps as FlowEdgeProps } from 'reactflow';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { JourneyType } from 'api/types';

import { BaseBuilder, BaseBuilderProps } from './base-builder';
import { JourneyEdge, JourneyExtraEdge, LoopEdge } from './edge';
import { JourneyBuilderActionsProvider } from './journey-builder-actions-context';
import { nodeTypes } from './node-types/';
import { PreviewContext, SelectedFormTemplatePreview } from './preview-journey-form';

export type EdgeProps = FlowEdgeProps;

type JourneyBuilderProps = Partial<BaseBuilderProps> & {
  journey: JourneyType;
  isPlanMode?: boolean;
  isPreviewMode?: boolean;
};

export const JourneyBuilder = memo(({ journey, ...rest }: JourneyBuilderProps) => {
  const [previewingId, setPreviewJourneyFormId] = useState('');

  const edgeTypes = useMemo(
    () => ({
      defaultEdge: (edgeProps: EdgeProps) => (
        <JourneyEdge journeyId={journey.shortId} {...edgeProps} />
      ),
      loopEdge: (edgeProps: EdgeProps) => <LoopEdge {...edgeProps} />,
      journeyExtraEdge: (edgeProps: EdgeProps) => <JourneyExtraEdge {...edgeProps} />,
    }),
    [journey.shortId]
  );

  return (
    <Container>
      <AnimatePresence>
        {previewingId && (
          <SelectedFormTemplatePreview
            onClose={() => setPreviewJourneyFormId('')}
            shortId={previewingId}
            journeyId={journey.shortId}
          />
        )}
      </AnimatePresence>

      <JourneyBuilderActionsProvider>
        <PreviewContext.Provider value={{ setPreviewJourneyFormId }}>
          <BaseBuilder
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            journeyId={journey.shortId}
            {...rest}
          />
        </PreviewContext.Provider>
      </JourneyBuilderActionsProvider>
    </Container>
  );
});

const Container = styled.div({
  flexGrow: 1,
  position: 'relative',
});
